import {forwardRef} from 'react'

import type {Persona} from '../types'

const formatTime = (seconds: number): string => {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0')
  const s = String(seconds % 60).padStart(2, '0')
  return `${m}:${s}`
}

export const ScoreCardImage = forwardRef<
  HTMLDivElement,
  {
    score: number
    persona: Persona
    solvedCount: number
    totalQuestions: number
    elapsedSeconds: number
    rankPercent: number
    caseNumber: string
  }
>(function ({score, persona, solvedCount, totalQuestions, elapsedSeconds, rankPercent, caseNumber}, ref) {
  return (
    <div
      ref={ref}
      style={{
        width: '600px',
        height: '314px',
        background: '#002414',
        position: 'relative',
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Neon green top bar */}
      <div style={{height: '2px', background: '#C6FF00', flexShrink: 0}} />

      {/* Right-side background rectangle */}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '200px',
          background: '#002D19',
        }}
      />

      {/* Moss logo watermark in right column */}
      <img
        src="/images/fraud-game/moss-watermark.png"
        alt=""
        style={{
          position: 'absolute',
          left: '53px',
          top: '33px',
          width: '293.6px',
          height: '247.248px',
          opacity: 0.4,
          pointerEvents: 'none',
        }}
      />

      <div style={{display: 'flex', flex: 1, padding: '36px 28px 32px 32px'}}>
        {/* Left column */}
        <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          {/* Top section */}
          <div>
            {/* Subject file + case number */}
            <div style={{marginBottom: '24px'}}>
              <div
                style={{
                  fontFamily: "'American Typewriter', 'Courier New', serif",
                  fontSize: '8px',
                  fontWeight: 400,
                  letterSpacing: '0.48px',
                  textTransform: 'uppercase',
                  color: '#426153',
                  marginBottom: '2px',
                }}
              >
                Subject file
              </div>
              <div
                style={{
                  fontFamily: "'American Typewriter', 'Courier New', serif",
                  fontSize: '8px',
                  fontWeight: 400,
                  letterSpacing: '0.48px',
                  textTransform: 'uppercase',
                  color: '#426153',
                }}
              >
                Case #{caseNumber}
              </div>
            </div>

            {/* Classification label */}
            <div
              style={{
                fontFamily: "'Systemia', sans-serif",
                fontSize: '8px',
                fontWeight: 600,
                letterSpacing: '0.48px',
                textTransform: 'uppercase',
                color: '#83958D',
                marginBottom: '12px',
              }}
            >
              Classification
            </div>

            {/* Persona title */}
            <div
              style={{
                fontFamily: "'Systemia', sans-serif",
                fontSize: '36px',
                fontWeight: 600,
                color: '#ffffff',
                lineHeight: 'normal',
              }}
            >
              {persona.title}
            </div>

            {/* Persona subline */}
            <div
              style={{
                fontFamily: "'Systemia', sans-serif",
                fontSize: '16px',
                fontWeight: 400,
                color: '#83958D',
                lineHeight: 'normal',
              }}
            >
              {persona.subline}
            </div>
          </div>

          {/* Stats row */}
          <div>
            <div style={{display: 'flex', gap: '0', marginBottom: '24px'}}>
              <StatBlock value={`${solvedCount}`} suffix={` /${totalQuestions}`} label="Cases solved" width="100px" />
              <StatBlock value={formatTime(elapsedSeconds)} suffix=" min" label="Time" width="100px" />
              <StatBlock value={`Top ${rankPercent}%`} label="Rank" width="100px" />
            </div>

            {/* Footer URL */}
            <div
              style={{
                fontFamily: "'Systemia', sans-serif",
                fontSize: '8px',
                fontWeight: 600,
                letterSpacing: '0.36px',
                textTransform: 'uppercase',
                color: '#426153',
              }}
            >
              getmoss.com/papertrails
            </div>
          </div>
        </div>

        {/* Right column — badge + score, centered within the green rectangle */}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            zIndex: 1,
          }}
        >
          {/* Badge */}
          <img src={persona.badgeImage} alt="" style={{width: '144px', height: '144px'}} />

          {/* Score */}
          <div style={{textAlign: 'center'}}>
            <div
              style={{
                fontFamily: "'Systemia', sans-serif",
                fontSize: '76px',
                fontWeight: 600,
                color: '#B9FF3F',
                lineHeight: '64px',
                letterSpacing: '4.56px',
                textTransform: 'uppercase',
                marginBottom: '2px',
              }}
            >
              {score}
            </div>
            <div
              style={{
                fontFamily: "'Systemia', sans-serif",
                fontSize: '8px',
                fontWeight: 600,
                letterSpacing: '0.48px',
                textTransform: 'uppercase',
                color: '#83958D',
              }}
            >
              Points
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

const StatBlock = ({value, suffix, label, width}: {value: string; suffix?: string; label: string; width?: string}) => (
  <div style={{width}}>
    <div
      style={{
        marginBottom: '0px',
        fontFamily: "'Systemia', sans-serif",
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.48px',
        textTransform: 'uppercase',
      }}
    >
      <span style={{color: '#B9FF3F'}}>{value}</span>
      {suffix && <span style={{color: '#83958D'}}>{suffix}</span>}
    </div>
    <div
      style={{
        fontFamily: "'Systemia', sans-serif",
        fontSize: '8px',
        fontWeight: 600,
        letterSpacing: '0.48px',
        textTransform: 'uppercase',
        color: '#83958D',
      }}
    >
      {label}
    </div>
  </div>
)
